import { useMemo } from 'react';
import { useLocation, useParams } from 'react-router-dom';

import { useSpendSummaryReportVisible } from 'shared/lib/hooks/useSpendSummaryReportVisible';
import { toNumber } from 'shared/lib/number/number';
import { useSpendSummaryDrillInData } from 'widgets/SpendSummaryReport/model/hooks/useSpendSummaryDrillInData';

import type {
    SpendInsightsSummaryDto,
    SpendProductsResponseDto,
    SpendSupplierRowDto,
} from 'entities/ReportSpendSummary';
import type { SpendDrillInLocationState } from 'widgets/SpendSummaryReport/model/types/spendDrillIn';

interface UseReportsPageSpendSummaryCategoryResult {
    isSpendSummaryVisible: boolean | undefined;
    categoryId: string | undefined;
    categoryName: string;
    loading: boolean;
    summary: SpendInsightsSummaryDto | null;
    bySupplier: SpendSupplierRowDto[];
    products: SpendProductsResponseDto | null;
    totalSpend: number;
    totalSpendPercent: number;
    orderCount: number;
    productsCount: number;
    dateFrom: string;
    dateTo: string;
    datePreset?: string;
    lastRequestParamsPresent: boolean;
    locationState: SpendDrillInLocationState;
}

export const useReportsPageSpendSummaryCategory =
    (): UseReportsPageSpendSummaryCategoryResult => {
        const { categoryId } = useParams<{ categoryId: string }>();
        const location = useLocation();
        const isSpendSummaryVisible = useSpendSummaryReportVisible();
        const locationState =
            (location.state as SpendDrillInLocationState | null) ?? {};
        const contextTotalSpend = toNumber(locationState.contextTotalSpend);

        const {
            loading,
            summary,
            byCategory,
            bySupplier,
            products,
            totalSpend,
            orderCount,
            productsCount,
            dateFrom,
            dateTo,
            datePreset,
            lastRequestParamsPresent,
        } = useSpendSummaryDrillInData({
            dimension: 'category',
            id: categoryId,
            scope: locationState.scope,
        });

        const categoryName = useMemo(() => {
            if (locationState.categoryTitle) {
                return locationState.categoryTitle;
            }

            const responseCategoryTitle = byCategory.find(
                (item) => item.categoryId === categoryId,
            )?.categoryTitle;

            if (responseCategoryTitle) {
                return responseCategoryTitle;
            }

            return categoryId ?? '';
        }, [byCategory, categoryId, locationState.categoryTitle]);

        const totalSpendPercent =
            contextTotalSpend > 0
                ? (totalSpend / contextTotalSpend) * 100
                : 100;

        return {
            isSpendSummaryVisible,
            categoryId,
            categoryName,
            loading,
            summary,
            bySupplier,
            products,
            totalSpend,
            totalSpendPercent,
            orderCount,
            productsCount,
            dateFrom,
            dateTo,
            datePreset,
            lastRequestParamsPresent,
            locationState,
        };
    };
